import { useRef, useState } from "react";
import { Camera, Loader2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/media";

function crop16x9(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth, H = img.naturalHeight;
      const sw = Math.min(W, (H * 16) / 9), sh = (sw * 9) / 16;
      const ow = Math.min(1920, sw), oh = Math.round((ow * 9) / 16);
      const c = document.createElement("canvas");
      c.width = ow; c.height = oh;
      const ctx = c.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, (W - sw) / 2, (H - sh) / 2, sw, sh, 0, 0, ow, oh);
      URL.revokeObjectURL(img.src);
      c.toBlob((b) => (b ? resolve(new File([b], "capa.jpg", { type: "image/jpeg" })) : reject(new Error("Falha ao processar a imagem"))), "image/jpeg", 0.9);
    };
    img.onerror = () => reject(new Error("Formato de imagem não suportado"));
    img.src = URL.createObjectURL(file);
  });
}

export function CoverPicker({ preview, onChange, onBusy }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Escolha uma imagem");
    setBusy(true); onBusy(true); setPct(0);
    try {
      const cropped = await crop16x9(file);
      const media = await uploadToCloudinary(cropped, "photo", setPct);
      onChange({ public_id: media.public_id, preview: URL.createObjectURL(cropped) });
    } catch (err) {
      toast.error(err.message || "Falha no envio da capa");
    } finally {
      setBusy(false); onBusy(false);
    }
  };

  return (
    <div className="relative aspect-video overflow-hidden rounded-2xl border border-dashed border-white/15 bg-white/5" data-testid="room-cover-picker">
      {preview ? <img src={preview} alt="Capa da sala" className="h-full w-full object-cover" data-testid="room-cover-preview" />
        : <div className="grid h-full place-items-center text-center text-xs text-muted-foreground"><div><ImagePlus className="mx-auto mb-2" size={26} />Sem foto: será usada uma capa automática</div></div>}
      {busy && (
        <div className="absolute inset-0 grid place-items-center bg-black/60" data-testid="room-cover-uploading">
          <div className="w-2/3 text-center text-sm font-bold"><Loader2 className="mx-auto mb-2 animate-spin" />A enviar… {pct}%
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15"><div className="h-full bg-[hsl(var(--gold))] transition-[width]" style={{ width: `${pct}%` }} /></div>
          </div>
        </div>
      )}
      <div className="absolute bottom-2 right-2 flex gap-2">
        {preview && !busy && (
          <button type="button" onClick={() => onChange(null)} aria-label="Remover capa" data-testid="room-cover-remove-button"
            className="grid h-9 w-9 place-items-center rounded-full bg-black/70 backdrop-blur transition-colors hover:bg-red-600"><X size={16} /></button>
        )}
        <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="room-cover-upload-button"
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--gold))] px-4 text-xs font-extrabold text-[#0B0F17] transition-transform active:scale-95 disabled:opacity-60">
          <Camera size={14} /> {preview ? "Trocar foto" : "Enviar foto"}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="room-cover-file-input" />
    </div>
  );
}
